import { plainText, splitNote, toDate } from "@nook/schema";
import { toUnicode } from "punycode";
const shortDate = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const shortDateYear = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" });
const fullDate = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
});
/** `16 Oct`, and `16 Oct 2025` outside this year, so last year's day never reads as this year's. Empty for a date it cannot read. */
export function formatShortDate(iso) {
    const date = toDate(iso);
    return isNaN(date.getTime()) ? "" : (date.getFullYear() === new Date().getFullYear() ? shortDate : shortDateYear).format(date);
}
/** Empty for a date it cannot read: `Intl` throws on one, and a post's date is the platform's string. */
export function formatFullDate(iso) {
    const date = toDate(iso);
    return isNaN(date.getTime()) ? "" : fullDate.format(date);
}
/** Today, Yesterday, then the short date, in the viewer's timezone. */
export function savedLabel(iso) {
    const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(iso).setHours(0, 0, 0, 0)) / 86_400_000);
    return days <= 0 ? "Today" : days === 1 ? "Yesterday" : formatShortDate(iso);
}
/** What kind of thing a nook is, for a row's meta line and the card's corner. */
export function nookKind(nook) {
    if (nook.type === "highlight")
        return "Highlight";
    if (nook.type === "screenshot")
        return "Screenshot";
    if (nook.platform === "youtube" || nook.platform === "tiktok")
        return "Video";
    if (nook.platform === "amazon")
        return "Product";
    if (nook.platform)
        return "Post";
    if (nook.sourceUrl)
        return "Article";
    return nook.imageUrl ? "Image" : "Note";
}
/** `₹1,299 on 28 Oct`: a product's price as read at the save, never as current. */
export function priceAsOf({ price, currency, priceAt }) {
    if (price === undefined)
        return null;
    const amount = currency
        ? new Intl.NumberFormat(undefined, { style: "currency", currency }).format(price)
        : String(price);
    return `${amount} on ${formatShortDate(priceAt)}`;
}
/** A playback position: `2:04`, or `1:02:04` past the hour. */
export function formatClock(seconds) {
    const [h, m, s] = [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, Math.floor(seconds) % 60];
    const pad = (n) => String(n).padStart(2, "0");
    return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
/** A transcript line's time: `00:56`, or `1:02:04` past the hour. */
export const transcriptTime = (seconds) => formatClock(seconds).padStart(5, "0");
/**
 * A video's transcript, timed when the server sent its lines, else its text as one untimed line.
 * `null` for anything else, and for a video whose content is its description.
 */
export function transcriptOf(nook) {
    if (nook.platform === "tiktok")
        return nook.media?.transcript ?? null;
    if (nook.platform !== "youtube" || nook.media?.transcriptUnavailable || !nook.content)
        return null;
    return nook.media?.transcript ?? [{ start: null, text: nook.content }];
}
/** `nytimes.com`, or `null` for a nook with no source. An IDN host reads in Unicode, not `xn--`. */
export function nookHost(nook) {
    if (!nook.sourceUrl)
        return null;
    try {
        return toUnicode(new URL(nook.sourceUrl).hostname).replace(/^www\./, "");
    }
    catch {
        return null;
    }
}
/** A note is all `user_note` (block 47); an image's or a page's `user_note` is an annotation. */
const noteText = (nook) => (nook.isNote ? nook.userNote : null);
function firstLine(text) {
    return text?.split("\n").find((line) => line.trim())?.trim() ?? null;
}
/**
 * The page's title, a note's title line, or else a note's opening line. The
 * editor still shows an untitled note's title field empty; only the card borrows.
 *
 * Truncated, because an excerpt's "title" is borrowed from its body and a
 * paragraph with no newline in it would otherwise be set as a heading.
 */
export function nookTitle(nook) {
    if (nook.sourceTitle)
        return nook.sourceTitle;
    // A page whose `<title>` never came back is still a page, not an untitled
    // note: a login wall, a 403 or a timeout leaves `source_title` null and the
    // nook is saved anyway (block 07, PRD §13), so the URL names it.
    const first = firstLine(nook.excerpt ?? noteText(nook));
    const line = (nook.isNote && !nook.excerpt && splitNote(nook.userNote).title) || (first && plainText(first)) || nook.sourceUrl;
    // An uncaptioned image is not an untitled *note* — it is the picture, and the
    // card already shows it (block 06).
    if (!line)
        return nook.imageUrl ? "Image" : "Untitled note";
    return line.length > 80 ? `${line.slice(0, 80).trimEnd()}…` : line;
}
/**
 * The line under the title: the summary once block 14 writes one, and until
 * then a note's body, whole even when the card borrowed its first line as the
 * title, so it reads as it does in the editor.
 */
export function nookBody(nook) {
    const body = (() => {
        if (nook.summary)
            return nook.summary;
        const text = nook.content ?? nook.excerpt;
        // A URL nook's body is its extracted article (block 08), and the user's own
        // annotation while extraction is still running or did not come back.
        if (nook.sourceUrl)
            return text ?? nook.userNote;
        if (!text && nook.isNote)
            return splitNote(nook.userNote).body;
        const own = text ?? noteText(nook);
        const rest = own?.slice(firstLine(own)?.length ?? 0).trim();
        // A note's first line is already its title; an older note's annotation and an image's caption stay as the body.
        return rest || (text || !own ? nook.userNote : null);
    })();
    // A highlight is the page's own words, quoted as the user selected them.
    if (!body || nook.type === "highlight")
        return body || null;
    return plainText(body) || null;
}
export { plainText };
/** An article's reading time at 238 words a minute, never under one. */
export const readingMinutes = (markdown) => Math.max(1, Math.round(plainText(markdown).split(" ").length / 238));
/**
 * An article from its first words: the logos, icons, badges and banners a scrape opens
 * with go, linked or not, along with empty links. Its pictures further down stay where they are; it
 * has no hero over it. A URL may hold one level of parentheses, as Wikipedia's `File:A_(b).jpg` do.
 */
export const withoutLeadImages = (markdown) => markdown.replace(/^(?:\s*(?:\[?!\[[^\]]*\]\((?:[^()]|\([^()]*\))*\)(?:\]\((?:[^()]|\([^()]*\))*\))?|\[\s*\]\((?:[^()]|\([^()]*\))*\)))+\s*/, "");
/**
 * An article without its own title on top: the header already sets it, and a scrape opens with the
 * page's `<title>` line ("Title | Site") and its H1 again. Only the first few blocks are looked at, so
 * a later paragraph that repeats the title stays.
 */
export function withoutTitle(markdown, title) {
    const key = (text) => plainText(text).toLowerCase();
    const t = key(title ?? "");
    const repeats = (block) => {
        const b = key(block);
        return b === t || (b.startsWith(t) && /^ [|·–—-] /.test(b.slice(t.length)));
    };
    // Odd parts are the blank lines between blocks; a dropped block leaves only extra blank lines.
    return t ? markdown.split(/(\n\s*\n)/).filter((part, i) => i % 2 || i > 10 || !repeats(part)).join("").trimStart() : markdown;
}
/** Full text for the detail panel: the note itself, not an excerpt of it. */
export function nookContent(nook) {
    return nook.content;
}
//# sourceMappingURL=nook-display.js.map